import {
  isPhysicallyActive,
  sharedActivityFrequencyStep,
  sharedProteinIntakeStep,
  sharedSleepQualityStep,
  sharedStressLevelStep,
} from '@/flow/shared/commonQuestions'
import type { FlowContext, Step } from '@/types'

const hasSelection = (key: string, value: string) => (ctx: FlowContext) => {
  const selections = ctx.vars[key]
  return Array.isArray(selections) && selections.includes(value)
}

const hasAnySelection = (key: string, values: string[]) => (ctx: FlowContext) => {
  const selections = ctx.vars[key]
  return Array.isArray(selections) && values.some((value) => selections.includes(value))
}

const hasCurrentStrategy = (ctx: FlowContext) => {
  const strategies = ctx.vars.estrategias_performance_atuais
  return Array.isArray(strategies) && strategies.length > 0
}

const hasEnergyComplaint = hasAnySelection('queixas_energia_performance', [
  'acorda_cansado',
  'queda_energia_dia',
  'cansaco_fisico_mental',
])

const hasRecoveryComplaint = hasAnySelection('queixas_energia_performance', [
  'recuperacao_lenta',
  'dor_pos_treino',
  'baixa_performance_treino',
])

const usesStimulants = (ctx: FlowContext) =>
  hasSelection('estrategias_performance_atuais', 'cafeina_pre_treino')(ctx) ||
  hasSelection('queixas_energia_performance', 'dependencia_estimulantes')(ctx)

/** Objective-specific steps: Energy & Performance (desempenho_fisico) */
export const energyPerformanceSteps: Step[] = [
  // ========== INTRO ==========
  {
    id: 'intro_energy_performance',
    type: 'message',
    content:
      'Vamos entender sua energia, recuperação e rotina para montar um protocolo mais direcionado ao seu desempenho.',
  },
  {
    id: 'energy_performance_goals',
    type: 'choice',
    inputKey: 'objetivos_energia_performance',
    multiple: true,
    minSelections: 1,
    content: 'O que você mais busca neste momento?',
    options: [
      { label: 'Mais disposição no dia a dia', value: 'mais_disposicao_dia' },
      { label: 'Melhorar rendimento nos treinos', value: 'melhorar_performance_treino' },
      { label: 'Ganhar força / explosão', value: 'ganhar_forca' },
      { label: 'Melhorar resistência / fôlego', value: 'melhorar_resistencia' },
      { label: 'Recuperar melhor após o treino', value: 'recuperar_mais_rapido' },
      { label: 'Apoiar ganho de massa magra', value: 'ganhar_massa_magra' },
      { label: 'Reduzir cansaço físico e mental', value: 'reduzir_cansaco' },
      { label: 'Depender menos de cafeína / estimulantes', value: 'menos_estimulantes' },
      { label: 'Apoiar testosterona / performance hormonal', value: 'suporte_testosterona' },
    ],
  },
  {
    id: 'energy_performance_concerns',
    type: 'choice',
    inputKey: 'queixas_energia_performance',
    multiple: true,
    minSelections: 1,
    content: 'Quais dessas situações mais combinam com você hoje?',
    options: [
      { label: 'Acordo cansado(a)', value: 'acorda_cansado' },
      { label: 'Tenho queda de energia ao longo do dia', value: 'queda_energia_dia' },
      { label: 'Sinto cansaço físico ou mental frequente', value: 'cansaco_fisico_mental' },
      {
        label: 'Meu rendimento no treino está abaixo do que gostaria',
        value: 'baixa_performance_treino',
      },
      { label: 'Demoro para me recuperar entre treinos', value: 'recuperacao_lenta' },
      { label: 'Fico muito dolorido(a) depois de treinar', value: 'dor_pos_treino' },
      { label: 'Falta motivação ou constância para treinar', value: 'baixa_motivacao' },
      {
        label: 'Sinto que dependo de café ou pré-treino para funcionar',
        value: 'dependencia_estimulantes',
      },
    ],
  },
  {
    id: 'energy_low_points',
    type: 'choice',
    inputKey: 'periodo_piora_energia',
    content: 'Em que momento a indisposição costuma aparecer mais?',
    condition: hasEnergyComplaint,
    options: [
      { label: 'Ao acordar', value: 'ao_acordar' },
      { label: 'Manhã', value: 'manha' },
      { label: 'Tarde', value: 'tarde' },
      { label: 'Noite', value: 'noite' },
      { label: 'O dia inteiro', value: 'dia_inteiro' },
      { label: 'Varia bastante', value: 'varia' },
    ],
  },
  {
    id: 'energy_routine_impact',
    type: 'choice',
    inputKey: 'impacto_rotina_energia',
    content: 'Quanto isso impacta sua rotina hoje?',
    options: [
      { label: 'Pouco', value: 'pouco' },
      { label: 'Moderadamente', value: 'moderado' },
      { label: 'Bastante', value: 'alto' },
      { label: 'Muito, limita meu dia ou meus treinos', value: 'muito_alto' },
    ],
  },

  // ========== HISTORY ==========
  {
    id: 'intro_energy_history',
    type: 'message',
    content: 'Agora vamos falar do histórico e do que pode estar influenciando esse quadro.',
  },
  {
    id: 'energy_onset',
    type: 'choice',
    inputKey: 'inicio_queixas_energia',
    content: 'Quando você percebeu piora da energia ou do desempenho?',
    options: [
      { label: 'Sempre tive essa tendência', value: 'sempre_tive' },
      { label: 'Nos últimos 3 meses', value: 'ultimos_3_meses' },
      { label: 'Entre 3 e 12 meses', value: '3_12_meses' },
      { label: 'Há mais de 1 ano', value: 'mais_1_ano' },
      { label: 'Após doença / infecção', value: 'apos_doenca' },
      { label: 'Após mudança de rotina ou trabalho', value: 'apos_mudanca_rotina' },
      { label: 'Após período de estresse ou sobrecarga', value: 'apos_estresse' },
      { label: 'Após ganho de peso ou sedentarismo', value: 'apos_sedentarismo' },
      { label: 'Após mudança hormonal', value: 'apos_mudanca_hormonal' },
      { label: 'Não sei ao certo', value: 'nao_sei' },
    ],
  },
  {
    id: 'energy_triggers',
    type: 'choice',
    inputKey: 'gatilhos_energia_performance',
    multiple: true,
    minSelections: 1,
    allowSkip: true,
    skipLabel: 'Não percebo gatilhos claros',
    content: 'O que parece piorar mais sua disposição ou performance?',
    options: [
      { label: 'Noites mal dormidas', value: 'sono_ruim' },
      { label: 'Estresse / excesso de trabalho', value: 'estresse' },
      { label: 'Treino em excesso / pouca recuperação', value: 'excesso_treino' },
      { label: 'Sedentarismo / falta de constância', value: 'falta_treino' },
      { label: 'Alimentação irregular', value: 'alimentacao_irregular' },
      { label: 'Baixa hidratação', value: 'baixa_hidratacao' },
      { label: 'Excesso de cafeína / estimulantes', value: 'excesso_estimulantes' },
      { label: 'Doença recente', value: 'doenca_recente' },
    ],
  },
  {
    id: 'energy_investigation',
    type: 'choice',
    inputKey: 'investigacao_cansaco_performance',
    content: 'Você já investigou esse cansaço ou queda de performance com exames ou consulta?',
    options: [
      { label: 'Sim, e houve alguma alteração', value: 'sim_com_alteracao' },
      { label: 'Sim, mas sem alterações relevantes', value: 'sim_sem_alteracao' },
      { label: 'Ainda não investiguei', value: 'nao' },
    ],
  },
  {
    id: 'energy_health_conditions',
    type: 'choice',
    inputKey: 'condicoes_associadas_energia',
    multiple: true,
    minSelections: 1,
    allowSkip: true,
    skipLabel: 'Nenhuma dessas',
    content: 'Você tem alguma dessas condições ou suspeitas?',
    options: [
      { label: 'Anemia / ferro baixo', value: 'anemia' },
      { label: 'Deficiência de vitaminas (B12, D, etc)', value: 'deficiencias' },
      { label: 'Alterações da tireoide', value: 'tireoide' },
      { label: 'Resistência à insulina / glicose alta', value: 'resistencia_insulina' },
      { label: 'Alterações hormonais', value: 'alteracoes_hormonais' },
      { label: 'Ansiedade / estresse elevado', value: 'ansiedade_estresse' },
      { label: 'Apneia / ronco importante', value: 'apneia' },
    ],
  },

  // ========== CURRENT STRATEGIES ==========
  {
    id: 'energy_current_strategies',
    type: 'choice',
    inputKey: 'estrategias_performance_atuais',
    multiple: true,
    minSelections: 1,
    allowSkip: true,
    skipLabel: 'Ainda não faço nada específico',
    content: 'O que você já usa ou faz hoje para melhorar energia ou performance?',
    options: [
      { label: 'Café / cafeína / pré-treino', value: 'cafeina_pre_treino' },
      { label: 'Creatina', value: 'creatina' },
      { label: 'Whey / proteína', value: 'proteina' },
      { label: 'Vitaminas, minerais', value: 'vitaminas_adaptogenos' },
      { label: 'Peptídeos', value: 'peptideos' },
      { label: 'Canetas emagrecedoras / GLP-1', value: 'canetas_emagrecedoras' },
      { label: 'Estratégia alimentar antes do treino', value: 'alimentacao_pre_treino' },
      { label: 'Acompanhamento nutricional', value: 'nutricionista' },
      { label: 'Tratamento hormonal ou médico', value: 'tratamento_hormonal' },
    ],
  },
  {
    id: 'energy_caffeine_consumption',
    type: 'choice',
    inputKey: 'consumo_cafeina_performance',
    content: 'Quanto de cafeína você costuma consumir por dia?',
    options: [
      { label: 'Nenhuma', value: 'nenhuma' },
      { label: '1 dose por dia', value: '1_dose' },
      { label: '2 a 3 doses por dia', value: '2_3_doses' },
      { label: '4 ou mais doses por dia', value: '4_mais_doses' },
      { label: 'Varia bastante', value: 'varia' },
    ],
  },
  {
    id: 'energy_stimulant_sensitivity',
    type: 'choice',
    inputKey: 'sensibilidade_estimulantes',
    content: 'Como seu corpo costuma responder a cafeína ou pré-treino?',
    condition: usesStimulants,
    options: [
      { label: 'Respondo bem, sem efeitos incômodos', value: 'boa_resposta' },
      { label: 'Fico acelerado(a), ansioso(a) ou com palpitações', value: 'ansiedade_palpitacao' },
      { label: 'Prejudica meu sono', value: 'prejudica_sono' },
      { label: 'Quase não sinto efeito', value: 'efeito_fraco' },
      { label: 'Só funciona quando aumento muito a dose', value: 'tolerancia_alta' },
    ],
  },
  {
    id: 'energy_strategy_duration',
    type: 'choice',
    inputKey: 'tempo_estrategias_performance',
    content: 'Há quanto tempo você segue essas estratégias?',
    condition: hasCurrentStrategy,
    options: [
      { label: 'Menos de 1 mês', value: 'menos_1_mes' },
      { label: '1 a 3 meses', value: '1_3_meses' },
      { label: '3 a 6 meses', value: '3_6_meses' },
      { label: '6 meses a 1 ano', value: '6_12_meses' },
      { label: 'Mais de 1 ano', value: 'mais_1_ano' },
    ],
  },
  {
    id: 'energy_strategy_results',
    type: 'choice',
    inputKey: 'resultado_estrategias_performance',
    content: 'Como você avalia os resultados até agora?',
    condition: hasCurrentStrategy,
    options: [
      { label: 'Melhorou bastante', value: 'melhorou' },
      { label: 'Melhorou um pouco', value: 'melhorou_pouco' },
      { label: 'Não percebi diferença', value: 'sem_diferenca' },
      { label: 'Piorou ou não consegui manter', value: 'piorou' },
    ],
  },

  // ========== LIFESTYLE ==========
  {
    id: 'intro_energy_lifestyle',
    type: 'message',
    content:
      'Por fim, algumas perguntas rápidas sobre rotina e hábitos que influenciam diretamente sua energia e recuperação.',
  },
  { ...sharedActivityFrequencyStep },
  {
    id: 'energy_training_type',
    type: 'choice',
    inputKey: 'modalidades_treino',
    multiple: true,
    minSelections: 1,
    content: 'Que tipo de treino ou atividade você faz com mais frequência?',
    condition: isPhysicallyActive,
    options: [
      { label: 'Musculação', value: 'musculacao' },
      { label: 'Corrida', value: 'corrida' },
      { label: 'Funcional / HIIT', value: 'funcional_hiit' },
      { label: 'Crossfit', value: 'crossfit' },
      { label: 'Ciclismo', value: 'ciclismo' },
      { label: 'Esportes', value: 'esportes' },
      { label: 'Caminhada', value: 'caminhada' },
      { label: 'Natação', value: 'natacao' },
      { label: 'Artes Marciais', value: 'artes_marciais' },
      { label: 'Beach tennis', value: 'beach_tennis' },
      { label: 'Triathlon', value: 'triathlon' },
      { label: 'Hyrox', value: 'hyrox' },
      { label: 'Yoga / Pilates', value: 'yoga_pilates' },
    ],
  },
  {
    id: 'energy_training_duration',
    type: 'choice',
    inputKey: 'duracao_treino',
    content: 'Em média, quanto tempo dura cada treino?',
    condition: isPhysicallyActive,
    options: [
      { label: 'Menos de 30 minutos', value: 'menos_30' },
      { label: '30 a 45 minutos', value: '30_45' },
      { label: '45 a 60 minutos', value: '45_60' },
      { label: 'Mais de 60 minutos', value: 'mais_60' },
    ],
  },
  {
    id: 'energy_training_time',
    type: 'choice',
    inputKey: 'periodo_treino',
    content: 'Em que período você costuma treinar?',
    condition: isPhysicallyActive,
    options: [
      { label: 'Manhã', value: 'manha' },
      { label: 'Tarde', value: 'tarde' },
      { label: 'Noite', value: 'noite' },
      { label: 'Varia bastante', value: 'varia' },
    ],
  },
  {
    id: 'energy_training_limitation',
    type: 'choice',
    inputKey: 'principal_limitacao_treino',
    content: 'No treino, o que mais costuma limitar seu desempenho?',
    condition: isPhysicallyActive,
    options: [
      { label: 'Falta de força / potência', value: 'forca' },
      { label: 'Falta de resistência / fôlego', value: 'resistencia' },
      { label: 'Recuperação lenta entre treinos', value: 'recuperacao' },
      { label: 'Baixa motivação / energia', value: 'motivacao_energia' },
      { label: 'Dores musculares ou desconforto', value: 'dores' },
      { label: 'Oscila bastante', value: 'oscila' },
    ],
  },
  {
    id: 'energy_recovery_time',
    type: 'choice',
    inputKey: 'tempo_recuperacao_treino',
    content: 'Quanto tempo você sente que precisa para recuperar bem de um treino mais intenso?',
    condition: (ctx) => isPhysicallyActive(ctx) && hasRecoveryComplaint(ctx),
    options: [
      { label: 'Menos de 24 horas', value: 'menos_24h' },
      { label: 'Entre 24 e 48 horas', value: '24_48h' },
      { label: 'Entre 48 e 72 horas', value: '48_72h' },
      { label: 'Mais de 72 horas', value: 'mais_72h' },
      { label: 'Varia bastante', value: 'varia' },
    ],
  },
  { ...sharedProteinIntakeStep },
  { ...sharedSleepQualityStep },
  { ...sharedStressLevelStep },
]
